import { Injectable, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { KeycloakService } from 'keycloak-angular';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { MessageService } from 'primeng/api';
import { environment } from '@env';
import {
  clearKeycloakTokenWithoutLogin,
  clearStoredImpersonationSession,
  delegationHandle,
  getStoredDelegationSession,
  hasStoredImpersonationSession,
  markStoredImpersonationSessionEnded,
  shouldBlockImpersonationFallback,
  tokenMatchesStoredImpersonationSession,
  updateStoredImpersonationTokens,
} from '@apruv-client/services';

const debugLog = (...args: any[]): void => {
  if (!environment.production) {
    console.log(...args);
  }
};

const debugWarn = (...args: any[]): void => {
  if (!environment.production) {
    console.warn(...args);
  }
};

const publicVerifyAppExactPaths = new Set([
  '/api/verify/v1/verify_app/workflow',
  '/api/verify/v1/verify_app/workflow/meta',
  '/api/verify/v1/verify_app/tts_session',
  '/api/verify/v1/verify_app/tts_ws',
  '/api/verify/v1/verify_app/tts_stream',
  '/api/verify/v1/verify_app/play_ht_session_id',
  '/api/verify/v1/verify_app/telemetry',
  '/api/verify/v1/verify_app/verification-started',
  '/api/verify/v1/verify_app/uploads/signed-url',
  '/api/verify/v1/verify_app/uploads/complete',
  '/api/verify/v1/verify_app/recording_validation_status',
  '/api/verify/v1/verify_app/verify_code',
  '/api/verify/v1/verify_app/v2/verify_code',
  '/api/verify/v1/verify_app/track-link-open',
  '/api/verify/v1/verify_app/company/api_key_type',
  '/api/verify/v1/verify_app/location/refresh_sales_capability',
  '/api/verify/v1/verify_app/sendLink',
  '/api/verify/v1/verify_app/send_reminder',
  '/api/verify/v1/verify_app/send_pre_service',
]);

const publicVerifyAppPrefixPaths = [
  '/api/verify/v1/verify_app/fieldroutes/',
  '/api/verify/v1/verify_app/cx-studio/',
  // Audit-reference pickup for PCM-streamed TTS clips — same unauthenticated
  // customer flow as /tts_stream itself.
  '/api/verify/v1/verify_app/tts_stream/audit/',
];

const publicVerifyLinkFlowFrontendExactPaths = new Set([
  '/api/frontend/v1/verify/feature-flags',
]);

function getRequestPath(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url.split('?')[0].split('#')[0];
  }
}

function isPublicVerifyApiRequest(url: string): boolean {
  const path = getRequestPath(url);

  if (!path.startsWith('/api/verify/v1/')) {
    return false;
  }

  if (path.startsWith('/api/verify/v1/verify_app/')) {
    return (
      publicVerifyAppExactPaths.has(path) ||
      publicVerifyAppPrefixPaths.some((prefix) => path.startsWith(prefix))
    );
  }

  return !path.startsWith('/api/verify/v1/reporting/');
}

function isVerifyLinkFlowRoute(routerUrl: string): boolean {
  const pathname =
    typeof window === 'undefined' ? '' : window.location.pathname;
  const href = typeof window === 'undefined' ? '' : window.location.href;

  return (
    routerUrl.includes('verifyLink=true') ||
    href.includes('verifyLink=true') ||
    routerUrl.startsWith('/v1/verify/verification') ||
    routerUrl.startsWith('/v2/verify/verification') ||
    pathname.startsWith('/v1/verify/verification') ||
    pathname.startsWith('/v2/verify/verification')
  );
}

function isVerifyLinkFlowPublicApiRequest(url: string): boolean {
  const path = getRequestPath(url);

  if (publicVerifyLinkFlowFrontendExactPaths.has(path)) {
    return true;
  }

  if (!path.startsWith('/api/verify/v1/')) {
    return false;
  }

  return !path.startsWith('/api/verify/v1/reporting/');
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private keycloak = inject(KeycloakService);
  private router = inject(Router);
  private messageService = inject(MessageService);

  // Track failed refresh attempts to prevent continuous loops
  private refreshAttempts = 0;
  private MAX_REFRESH_ATTEMPTS = 2;
  // Proactive refresh threshold: refresh if token expires in less than 600 seconds (10 minutes)
  // With 20-minute (1200s) tokens, this refreshes when there's 600 seconds remaining (50% of lifetime)
  // This ensures tokens are always very fresh and reduces the risk of expired tokens during long uploads
  // For long uploads (up to ~10 minutes), this ensures tokens remain valid throughout the process
  private readonly TOKEN_REFRESH_THRESHOLD_SECONDS = 600;
  // Reactive refresh minimum validity: when handling 401/403 errors, ensure token has at least 30s remaining
  // This is used for emergency token refresh when proactive refresh didn't catch the expiration
  private readonly REACTIVE_REFRESH_MIN_VALIDITY_SECONDS = 30;
  // Track ongoing refresh to prevent concurrent refresh calls
  private refreshPromise: Promise<boolean> | null = null;
  private impersonationFailureInProgress = false;

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const isVerifyLinkFlow = isVerifyLinkFlowRoute(this.router.url);
    const requestPath = getRequestPath(request.url);
    const delegatedSession = getStoredDelegationSession();
    const isAPIRequest = requestPath.startsWith('/api/') || request.url.startsWith(environment.billingApiUrl);
    if (
      delegatedSession &&
      isAPIRequest &&
      request.method !== 'GET' &&
      request.method !== 'HEAD' &&
      request.method !== 'OPTIONS'
    ) {
      return throwError(() => new Error('Delegated access is read-only'));
    }

    // Skip auth for public endpoints that don't require authentication
    // Public verify endpoints use their own auth mechanism (API keys, verify codes)
    // All review-app endpoints are public for unauthenticated review flows
    // Registration endpoints are public (company lookup, sales rep registration)
    if (
      request.url.includes('/api/auth/keycloak') ||
      request.url.includes('/api/auth/logout') ||
      isPublicVerifyApiRequest(request.url) ||
      (isVerifyLinkFlow && isVerifyLinkFlowPublicApiRequest(request.url)) ||
      request.url.includes('/api/frontend/v1/review-app/') ||
      request.url.includes(
        '/api/frontend/v1/rate/rate-config/rate-message-custom-fields'
      ) ||
      request.url.includes('/api/frontend/v1/sys/company/id/') ||
      request.url.includes('/api/auth/register')
    ) {
      return next.handle(request);
    }

    if (shouldBlockImpersonationFallback()) {
      this.router
        .navigateByUrl('/access-denied?reason=impersonation-ended')
        .catch(() => undefined);
      return throwError(() => new Error('Impersonation session ended'));
    }

    // If Keycloak isn't initialized (public routes), skip auth
    const keycloakInstance = this.keycloak.getKeycloakInstance();
    if (!keycloakInstance || !keycloakInstance.authenticated) {
      return next.handle(request);
    }

    // Proactively check and refresh token before making the request
    return from(this.ensureTokenValid()).pipe(
      switchMap(() => {
        // Add auth header to the request
        const authRequest = this.addAuthHeader(request);
        return next.handle(authRequest);
      }),
      catchError((error: HttpErrorResponse | Error) => {
        // Handle proactive token refresh failures (non-HTTP errors)
        if (!(error instanceof HttpErrorResponse)) {
          debugWarn(
            '[AUTH.INTERCEPTOR] Proactive token validation failed:',
            error.message
          );

          if (
            this.impersonationFailureInProgress ||
            hasStoredImpersonationSession() ||
            shouldBlockImpersonationFallback()
          ) {
            return throwError(() => error);
          }

          // Token refresh failed before request - trigger session expired flow
          if (
            this.router.url !== '/login' &&
            !isVerifyLinkFlow &&
            this.router.url !== '/'
          ) {
            sessionStorage.setItem('redirectAfterLogin', this.router.url);
            this.showSessionError();
            setTimeout(() => {
              this.keycloak.login({
                redirectUri: window.location.origin + this.router.url,
              });
            }, 1000);
          }

          return throwError(() => error);
        }

        // Skip auth interceptor for public endpoints to prevent loops
        // Public verify endpoints use their own auth mechanism (API keys, verify codes)
        // All review-app endpoints are public for unauthenticated review flows
        if (
          request.url.includes('/api/auth/keycloak') ||
          request.url.includes('/api/auth/logout') ||
          isPublicVerifyApiRequest(request.url) ||
          (isVerifyLinkFlow && isVerifyLinkFlowPublicApiRequest(request.url)) ||
          request.url.includes('/api/frontend/v1/review-app/') ||
          request.url.includes(
            '/api/frontend/v1/rate/rate-config/rate-message-custom-fields'
          )
        ) {
          return throwError(() => error);
        }

        // Check if the error is due to authentication (fallback reactive refresh)
        if (error.status === 401 || error.status === 403) {
          debugWarn(
            `[AUTH.INTERCEPTOR] Auth error (${error.status}):`,
            request.method,
            request.url
          );

          if (hasStoredImpersonationSession()) {
            debugWarn(
              '[AUTH.INTERCEPTOR] Impersonated request was rejected; not refreshing through Keycloak:',
              request.method,
              request.url
            );
            return throwError(() => error);
          }

          if (shouldBlockImpersonationFallback()) {
            return throwError(() => error);
          }

          // Don't attempt refresh if we've already tried multiple times
          if (this.refreshAttempts >= this.MAX_REFRESH_ATTEMPTS) {
            debugWarn(
              `[AUTH.INTERCEPTOR] Max refresh attempts (${this.MAX_REFRESH_ATTEMPTS}) reached, will not retry`
            );
            this.refreshAttempts = 0; // Reset for next sequence
            this.showSessionError();
            return throwError(() => error);
          }

          // Log attempted token refresh to Sentry
          Sentry.addBreadcrumb({
            category: 'auth',
            message: `Reactive token refresh after ${
              error.status
            } error (attempt ${this.refreshAttempts + 1})`,
            level: 'info',
          });

          this.refreshAttempts++;

          // Try to update the token (reactive fallback)
          return from(this.updateToken()).pipe(
            switchMap((success) => {
              if (success) {
                // If token refresh was successful, retry the request
                debugLog(
                  '[AUTH.INTERCEPTOR] Reactive token refresh successful, retrying request'
                );
                Sentry.addBreadcrumb({
                  category: 'auth',
                  message:
                    'Reactive token refresh successful, retrying request',
                  level: 'info',
                });
                this.refreshAttempts = 0; // Reset on success
                return next.handle(this.addAuthHeader(request));
              } else {
                debugWarn(
                  '[AUTH.INTERCEPTOR] Reactive token refresh failed, handling auth failure'
                );
                // If token refresh failed, save current URL and redirect to login
                // but only if we're not already at the login page
                Sentry.addBreadcrumb({
                  category: 'auth',
                  message: 'Token refresh failed, handling auth failure',
                  level: 'warning',
                });

                this.refreshAttempts = 0; // Reset for next sequence

                if (hasStoredImpersonationSession()) {
                  this.handleImpersonationSessionEnded();
                  return throwError(() => error);
                }

                if (shouldBlockImpersonationFallback()) {
                  return throwError(() => error);
                }

                // Check if we're on a page that needs authentication
                if (
                  this.router.url !== '/login' &&
                  !isVerifyLinkFlow &&
                  this.router.url !== '/'
                ) {
                  // Save the current URL for redirect after login
                  sessionStorage.setItem('redirectAfterLogin', this.router.url);

                  // Show a friendly message about session expiry
                  this.showSessionError();

                  // Redirect to login after a delay
                  setTimeout(() => {
                    this.keycloak.login({
                      redirectUri: window.location.origin + this.router.url,
                    });
                  }, 1000);
                }

                return throwError(() => error);
              }
            }),
            catchError((refreshError) => {
              // This catch also observes errors from the retried API request
              // returned by switchMap above. An HTTP failure there means the
              // token refresh completed and the endpoint itself rejected or
              // failed the request; it is not evidence that the session
              // expired. Preserve that response for the feature to handle.
              if (
                refreshError instanceof HttpErrorResponse &&
                refreshError.status !== 401
              ) {
                return throwError(() => refreshError);
              }

              // If there was an error refreshing the token
              console.error(
                '[AUTH.INTERCEPTOR] Error refreshing token (reactive):',
                refreshError
              );
              this.refreshAttempts = 0; // Reset for next sequence

              Sentry.captureException(
                new Error(
                  `Token refresh error: ${
                    refreshError.message || 'Unknown error'
                  }`
                )
              );

              if (hasStoredImpersonationSession()) {
                this.handleImpersonationSessionEnded();
                return throwError(() => error);
              }

              if (shouldBlockImpersonationFallback()) {
                return throwError(() => error);
              }

              // Only save URL and redirect if we're not already at login
              if (
                this.router.url !== '/login' &&
                !isVerifyLinkFlow &&
                this.router.url !== '/'
              ) {
                // Save the current URL for redirect after login
                sessionStorage.setItem('redirectAfterLogin', this.router.url);

                // Show a friendly message about session expiry
                this.showSessionError();

                // Redirect to login after a delay
                setTimeout(() => {
                  this.keycloak.login({
                    redirectUri: window.location.origin + this.router.url,
                  });
                }, 1000);
              }

              return throwError(() => error);
            })
          );
        }
        return throwError(() => error);
      })
    );
  }

  // Helper method to show a user-friendly session error
  private showSessionError() {
    this.messageService.add({
      severity: 'info',
      summary: 'Session Expired',
      detail: 'Your session has expired. Redirecting you to login...',
      life: 5000,
    });
  }

  /**
   * Proactively ensures the token is valid before making requests.
   * Checks if token expires within threshold and refreshes if needed.
   * Uses a single promise to handle concurrent requests efficiently.
   *
   * IMPORTANT: This method throws if token cannot be validated/refreshed,
   * preventing requests with invalid tokens from being sent.
   */
  private async ensureTokenValid(): Promise<void> {
    // Check if user is logged in
    const isLoggedIn = await this.keycloak.isLoggedIn();
    if (!isLoggedIn) {
      return;
    }

    this.verifyAndPersistImpersonationToken();

    // Check if token needs refresh based on expiration time
    const expirationTime = this.getTokenExpirationTime();
    if (hasStoredImpersonationSession()) {
      if (expirationTime <= 0) {
        this.handleImpersonationSessionEnded();
        throw new Error('Impersonation session expired');
      }
      return;
    }

    if (expirationTime < this.TOKEN_REFRESH_THRESHOLD_SECONDS) {
      // Token expires soon or is expired - refresh proactively
      const timeRemaining =
        expirationTime > 0 ? `${expirationTime}s` : 'expired';
      debugLog(
        `[AUTH.INTERCEPTOR] Token refresh needed (${timeRemaining} remaining), refreshing proactively`
      );

      try {
        const refreshed = await this.refreshToken();
        if (refreshed) {
          // Small delay to ensure Keycloak has updated the token instance
          await new Promise((resolve) => setTimeout(resolve, 50));
          this.verifyAndPersistImpersonationToken();
          const newExpirationTime = this.getTokenExpirationTime();
          debugLog(
            `[AUTH.INTERCEPTOR] Token refreshed successfully! New expiration: ${newExpirationTime}s remaining`
          );
          Sentry.addBreadcrumb({
            category: 'auth',
            message: `Token refreshed proactively (was expiring in ${timeRemaining}, now ${newExpirationTime}s)`,
            level: 'info',
          });
        }

        // Verify token is now valid after refresh attempt
        const newExpirationTime = this.getTokenExpirationTime();
        if (newExpirationTime <= 0) {
          const error = new Error('Token still expired after refresh attempt');
          console.error(
            '[AUTH.INTERCEPTOR] Token still expired after refresh:',
            newExpirationTime
          );
          Sentry.captureException(error);
          throw error;
        }
      } catch (error) {
        console.error('[AUTH.INTERCEPTOR] Error refreshing token:', error);
        Sentry.captureException(
          new Error(
            `Proactive token refresh error: ${
              error instanceof Error ? error.message : 'Unknown error'
            }`
          )
        );
        if (hasStoredImpersonationSession()) {
          this.handleImpersonationSessionEnded();
        }
        // Re-throw to prevent request with invalid token
        throw error;
      }
    }
  }

  /**
   * Refreshes the token, using a single promise to prevent concurrent refreshes
   */
  private async refreshToken(): Promise<boolean> {
    if (hasStoredImpersonationSession()) {
      this.handleImpersonationSessionEnded();
      return false;
    }

    // If a refresh is already in progress, wait for it
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Start a new refresh
    this.refreshPromise = (async () => {
      try {
        const refreshed = await this.keycloak.updateToken(
          this.TOKEN_REFRESH_THRESHOLD_SECONDS
        );
        this.verifyAndPersistImpersonationToken();
        this.refreshPromise = null; // Clear the promise on completion
        return refreshed;
      } catch (error) {
        this.refreshPromise = null; // Clear the promise on error
        console.error('[AUTH.INTERCEPTOR] Token refresh failed:', error);
        throw error;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Gets the token expiration time in seconds
   */
  private getTokenExpirationTime(): number {
    try {
      const kc = this.keycloak.getKeycloakInstance();
      if (!kc || !kc.tokenParsed || !kc.tokenParsed.exp) {
        debugWarn(
          '[AUTH.INTERCEPTOR] Unable to get token expiration time: token not available or not parsed'
        );
        return 0;
      }

      const expirationTimestamp = kc.tokenParsed.exp;
      const currentTimestamp = Math.floor(Date.now() / 1000);
      const timeRemaining = expirationTimestamp - currentTimestamp;

      return timeRemaining;
    } catch (error) {
      console.error(
        '[AUTH.INTERCEPTOR] Error getting token expiration time:',
        error
      );
      return 0;
    }
  }

  /**
   * Reactive token refresh (fallback when proactive refresh didn't catch it)
   */
  private updateToken(): Promise<boolean> {
    if (hasStoredImpersonationSession()) {
      this.handleImpersonationSessionEnded();
      return Promise.resolve(false);
    }

    return new Promise<boolean>((resolve) => {
      this.keycloak
        .updateToken(this.REACTIVE_REFRESH_MIN_VALIDITY_SECONDS)
        .then((refreshed) => {
          if (refreshed) {
            debugLog('[AUTH.INTERCEPTOR] Reactive token refresh successful');
          }
          this.verifyAndPersistImpersonationToken();
          resolve(true);
        })
        .catch((error) => {
          console.error(
            '[AUTH.INTERCEPTOR] Reactive token refresh failed:',
            error
          );
          resolve(false);
        });
    });
  }

  // Helper method to add the auth header to the request
  private addAuthHeader(request: HttpRequest<any>): HttpRequest<any> {
    this.verifyAndPersistImpersonationToken();
    const token = this.keycloak.getKeycloakInstance().token;
    if (!token) {
      debugWarn('[AUTH.INTERCEPTOR] No token available for auth header');
      return request;
    }

    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    const handle = delegationHandle();
    const requestPath = getRequestPath(request.url);
    const delegatedAPIRead =
      (request.method === 'GET' || request.method === 'HEAD') &&
      (requestPath.startsWith('/api/frontend/v1/') ||
        requestPath.startsWith('/api/verify/v1/') ||
        requestPath === '/api/me/access' ||
        request.url.startsWith(environment.billingApiUrl));
    if (handle && delegatedAPIRead) {
      headers['X-Apruv-Delegation'] = handle;
    }
    return request.clone({ setHeaders: headers });
  }

  private verifyAndPersistImpersonationToken(): void {
    if (!hasStoredImpersonationSession()) {
      return;
    }

    const keycloakInstance = this.keycloak.getKeycloakInstance();
    if (!tokenMatchesStoredImpersonationSession(keycloakInstance?.token)) {
      this.handleImpersonationSessionEnded();
      throw new Error('Impersonation session changed users');
    }

    updateStoredImpersonationTokens(
      keycloakInstance?.token,
      keycloakInstance?.idToken,
      keycloakInstance?.refreshToken
    );
  }

  private handleImpersonationSessionEnded(): void {
    this.impersonationFailureInProgress = true;
    markStoredImpersonationSessionEnded();
    clearStoredImpersonationSession();
    clearKeycloakTokenWithoutLogin(this.keycloak.getKeycloakInstance());
    this.messageService.add({
      severity: 'warn',
      summary: 'Impersonation ended',
      detail:
        'The impersonation session ended. Start a new session from Admin.',
      life: 5000,
    });
    this.router
      .navigateByUrl('/access-denied?reason=impersonation-ended')
      .catch(() => undefined);
  }
}
